import * as React from 'react'
import PropTypes from 'prop-types'
import { Controller, FormProvider, useFormContext } from 'react-hook-form'
import { Label } from './label'

const Form = FormProvider

const FormFieldContext = React.createContext({})

function FormField({ ...props }) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  )
}

FormField.propTypes = {
  name: PropTypes.string.isRequired
}

function useFormField() {
  const fieldContext = React.useContext(FormFieldContext)
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>')
  }

  return {
    name: fieldContext.name,
    ...fieldState
  }
}

const FormItem = React.forwardRef(({ className, ...props }, ref) => {
  return <div ref={ref} className={`space-y-2 ${className || ''}`} {...props} />
})
FormItem.displayName = 'FormItem'
FormItem.propTypes = {
  className: PropTypes.string
}

const FormLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { error } = useFormField()

  return <Label ref={ref} className={`${error ? 'text-red-500' : ''} ${className || ''}`} {...props} />
})
FormLabel.displayName = 'FormLabel'
FormLabel.propTypes = {
  className: PropTypes.string
}

const FormControl = React.forwardRef(({ ...props }, ref) => {
  const { error } = useFormField()

  return <div ref={ref} aria-invalid={!!error} {...props} />
})
FormControl.displayName = 'FormControl'

const FormDescription = React.forwardRef(({ className, ...props }, ref) => {
  return <p ref={ref} className={`text-sm text-gray-500 ${className || ''}`} {...props} />
})
FormDescription.displayName = 'FormDescription'
FormDescription.propTypes = {
  className: PropTypes.string
}

const FormMessage = React.forwardRef(({ className, children, ...props }, ref) => {
  const { error } = useFormField()
  const body = error ? String(error?.message) : children

  if (!body) {
    return null
  }

  return (
    <p ref={ref} className={`text-sm font-medium text-red-500 ${className || ''}`} {...props}>
      {body}
    </p>
  )
})
FormMessage.displayName = 'FormMessage'
FormMessage.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
}

export { useFormField, Form, FormItem, FormLabel, FormControl, FormDescription, FormMessage, FormField }
